// src/components/Auth/ProtectedRoute.js
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../LoadingSpinner';
import logger from '../../utils/logger';

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, currentUser, loading, verifyToken, logout, getUserInfo } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [tokenError, setTokenError] = useState(null);

  // Vérifier l'état du token JWT seulement au montage, pas à chaque changement
  useEffect(() => {
    // Utiliser une variable pour suivre si le composant est monté
    let isMounted = true;
    
    const checkAuthentication = async () => {
      // Vérifier d'abord si un token existe
      const tokenStatus = verifyToken();
      if (!tokenStatus.valid && isMounted) {
        setTokenError(tokenStatus.message);
        // Déconnecter l'utilisateur si le token est invalide
        logout();
        return;
      }
      
      // Même si le token semble valide, vérifions avec le serveur
      // mais seulement au premier chargement, pas à chaque re-rendu
      if (isAuthenticated && isMounted) {
        try {
          // Tenter de rafraîchir les informations utilisateur depuis le serveur
          // sans forcer des appels répétés
          const userData = await getUserInfo();
          if (!userData && isMounted) {
            // Si aucune donnée utilisateur n'est retournée, déconnecter
            setTokenError("Votre session a expiré ou est invalide. Veuillez vous reconnecter.");
            logout();
          }
        } catch (error) {
          if (isMounted) {
            logger.error("Erreur lors de la vérification de l'authentification:", error);
            setTokenError("Erreur de connexion au serveur. Veuillez vous reconnecter.");
            logout();
          }
        }
      }
    };
    
    // Effectuer la vérification unique
    checkAuthentication();
    
    // Nettoyer pour éviter les mises à jour sur un composant démonté
    return () => {
      isMounted = false;
    };
    
    // Dépendances réduites pour éviter des appels répétés
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Si l'authentification est en cours de vérification, afficher un spinner
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }
  
  // Afficher un message d'erreur si le token est invalide
  if (tokenError) {
    return (
      <div className="flex flex-col justify-center items-center h-screen">
        <div className="bg-red-100 text-red-700 p-4 rounded-md mb-4">
          {tokenError}
        </div>
        <button 
          onClick={() => navigate('/login')} 
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
        >
          Se reconnecter
        </button>
      </div>
    );
  }

  // Vérifier si l'utilisateur est connecté
  if (!isAuthenticated) {
    // Rediriger vers la page de connexion et stocker l'URL d'origine
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Vérifier si la route nécessite des droits d'admin
  if (requireAdmin && !currentUser.is_admin) {
    // Rediriger vers la page principale si l'utilisateur n'est pas admin
    return <Navigate to="/chatbot" replace />;
  }

  // Si tout est bon, afficher le contenu
  return children;
};

export default ProtectedRoute;
